import {
    ArrowLeftIcon,
    CheckCircleIcon,
    DocumentCheckIcon,
    ExclamationTriangleIcon,
    StarIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { AdminApiService } from '../../utils/api';

const CreateAgent = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    location: '',
    membership: 'basic',
    initialDeposit: '',
    referralCode: '',
    aadharNumber: '',
    panNumber: '',
    licenseNumber: '',
    gstNumber: '',
    profileImage: null
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    let parsedValue = type === 'file' ? files[0] : value;
    if (name === 'phone' && type !== 'file') {
      parsedValue = String(value || '').replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone)) newErrors.phone = 'Phone number must be exactly 10 digits';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (!formData.confirmPassword.trim()) newErrors.confirmPassword = 'Confirm password is required';
    else if (formData.confirmPassword !== formData.password) newErrors.confirmPassword = 'Passwords do not match';

    if (formData.aadharNumber.trim() && !/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ''))) {
      newErrors.aadharNumber = 'Aadhar number must be 12 digits';
    }

    if (formData.panNumber.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.toUpperCase())) {
      newErrors.panNumber = 'Invalid PAN format';
    }
    
    if (formData.membership === 'premium' && !formData.initialDeposit) {
      newErrors.initialDeposit = 'Initial deposit is required for premium membership';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.replace(/\D/g, ''),
        password: formData.password,
        location: formData.location.trim(),
        membership: formData.membership,
        initialDeposit: formData.initialDeposit ? Number(formData.initialDeposit) : undefined,
        referralCode: formData.referralCode?.trim() || undefined,
        aadharNumber: formData.aadharNumber?.replace(/\s/g, '') || undefined,
        panNumber: formData.panNumber?.toUpperCase() || undefined,
        licenseNumber: formData.licenseNumber?.trim() || undefined,
        gstNumber: formData.gstNumber?.trim().toUpperCase() || undefined
      };

      await AdminApiService.createAgent(payload);
      
      toast.success(`Agent ${formData.name} created successfully!`);
      navigate('/agents');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create agent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/agents')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Create New Agent
          </h1>
          <p className="text-gray-600 mt-2">
            Add a new agent to your marketplace
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <UserIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`input-field ${errors.name ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="Enter full name"
              />
              {errors.name && <p className="text-danger-600 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`input-field ${errors.email ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="Enter email address"
              />
              {errors.email && <p className="text-danger-600 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`input-field ${errors.phone ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="9876543210"
                maxLength={10}
              />
              {errors.phone && <p className="text-danger-600 text-sm mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`input-field ${errors.password ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="Minimum 6 characters"
              />
              {errors.password && <p className="text-danger-600 text-sm mt-1">{errors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`input-field ${errors.confirmPassword ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="Re-enter password"
              />
              {errors.confirmPassword && <p className="text-danger-600 text-sm mt-1">{errors.confirmPassword}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className={`input-field ${errors.location ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="City, State"
              />
              {errors.location && <p className="text-danger-600 text-sm mt-1">{errors.location}</p>}
            </div>
          </div>
        </motion.div>

        {/* Membership Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-warning-100 rounded-lg">
              <StarIcon className="w-6 h-6 text-warning-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Membership & Payment</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Membership Type *
              </label>
              <select
                name="membership"
                value={formData.membership}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="basic">Basic (Free)</option>
                <option value="premium">Premium (₹5,000/month)</option>
              </select>
            </div>

            {formData.membership === 'premium' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Initial Deposit *
                </label>
                <input
                  type="number"
                  name="initialDeposit"
                  value={formData.initialDeposit}
                  onChange={handleInputChange}
                  className={`input-field ${errors.initialDeposit ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="5000"
                  min="5000"
                />
                {errors.initialDeposit && <p className="text-danger-600 text-sm mt-1">{errors.initialDeposit}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referral Code (Optional)
              </label>
              <input
                type="text"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Enter referral code"
              />
            </div>
          </div>

          {formData.membership === 'premium' && (
            <div className="mt-6 p-4 bg-warning-50 border border-warning-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-warning-800">Premium Membership Benefits</h4>
                  <ul className="text-sm text-warning-700 mt-2 space-y-1">
                    <li>• Priority listing placement</li>
                    <li>• Advanced analytics dashboard</li>
                    <li>• Dedicated support manager</li>
                    <li>• Higher commission rates</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Document Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-secondary-100 rounded-lg">
              <DocumentCheckIcon className="w-6 h-6 text-secondary-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Document Verification</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aadhar Number (Optional)
              </label>
              <input
                type="text"
                name="aadharNumber"
                value={formData.aadharNumber}
                onChange={handleInputChange}
                className={`input-field ${errors.aadharNumber ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="1234 5678 9012"
                maxLength="14"
              />
              {errors.aadharNumber && <p className="text-danger-600 text-sm mt-1">{errors.aadharNumber}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PAN Number (Optional)
              </label>
              <input
                type="text"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleInputChange}
                className={`input-field ${errors.panNumber ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="ABCDE1234F"
                style={{ textTransform: 'uppercase' }}
                maxLength="10"
              />
              {errors.panNumber && <p className="text-danger-600 text-sm mt-1">{errors.panNumber}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driving License Number
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Enter license number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GST Number (Optional)
              </label>
              <input
                type="text"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Enter GST number"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <input
              type="file"
              name="profileImage"
              onChange={handleInputChange}
              accept="image/*"
              className="input-field"
            />
            <p className="text-sm text-gray-500 mt-1">Upload a profile picture (JPG, PNG, max 5MB)</p>
          </div>
        </motion.div>

        {/* Form Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200"
        >
          <button
            type="button"
            onClick={() => navigate('/agents')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center space-x-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                <span>Create Agent</span>
              </>
            )}
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default CreateAgent;